import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { FiTrendingUp, FiCheckCircle, FiClock, FiUsers, FiLayers } from 'react-icons/fi';

const ProgressOverview = () => {
  const [stats, setStats] = useState(null);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsResponse, progressResponse] = await Promise.all([
        axios.get('/tasks/stats/overview'),
        axios.get('/users/progress/overview'),
      ]);
      setStats(statsResponse.data);
      setUserProgress(progressResponse.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300"></div>
        </div>
      </>
    );
  }

  const completionRate = stats.totalTasks > 0
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Progress</h1>
          <p className="text-dark-100 text-sm">Team & user progress</p>
        </div>

        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Total Tasks</span>
              <FiLayers className="text-purple-300" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.totalTasks}</p>
          </div>
          
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Completed</span>
              <FiCheckCircle className="text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.completedTasks}</p>
            <p className="text-sm text-dark-100 mt-1">{completionRate}% completion rate</p>
          </div>
          
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">In Progress</span>
              <FiTrendingUp className="text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.inProgressTasks}</p>
          </div>
          
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Pending</span>
              <FiClock className="text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.pendingTasks}</p>
          </div>
        </div>

        {/* Tasks by Status Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Tasks by Status</h2>
            <div className="space-y-4">
              {stats.tasksByStatus.map((item) => {
                const percentage = stats.totalTasks > 0
                  ? Math.round((item.count / stats.totalTasks) * 100)
                  : 0;
                
                return (
                  <div key={item._id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-dark-100 capitalize">{item._id || 'N/A'}</span>
                      <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-dark-300 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          item._id === 'completed' ? 'bg-green-400' :
                          item._id === 'in-progress' ? 'bg-blue-400' :
                          item._id === 'pending' ? 'bg-yellow-400' :
                          'bg-purple-300'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Tasks by Priority</h2>
            <div className="space-y-4">
              {stats.tasksByPriority.map((item) => {
                const percentage = stats.totalTasks > 0
                  ? Math.round((item.count / stats.totalTasks) * 100)
                  : 0;
                
                const colorClass = item._id === 'urgent' ? 'bg-red-400' :
                  item._id === 'high' ? 'bg-orange-400' :
                  item._id === 'medium' ? 'bg-yellow-400' :
                  'bg-blue-400';
                
                return (
                  <div key={item._id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-dark-100 capitalize">{item._id || 'N/A'}</span>
                      <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-dark-300 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${colorClass}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* User Progress Table */}
        <div className="bg-stroke border border-stroke rounded-lg p-6">
          <div className="flex items-center mb-6">
            <FiUsers className="text-purple-300 mr-2" />
            <h2 className="text-xl font-semibold text-white">Individual User Progress</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-300">
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">User</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">Role</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">Total Tasks</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">Completed</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">In Progress</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">Pending</th>
                  <th className="text-left py-3 px-4 text-dark-100 font-medium">Avg Progress</th>
                </tr>
              </thead>
              <tbody>
                {userProgress.filter((item) => item?.user).map((item, index) => {
                  const name = item.user?.name ?? '';
                  const initial = name ? name.charAt(0).toUpperCase() : (item.user?.email?.charAt(0)?.toUpperCase() ?? '?');
                  return (
                  <tr key={item.user?._id ?? index} className="border-b border-dark-300 hover:bg-background/50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-purple-300 flex items-center justify-center text-white font-semibold">
                          {initial}
                        </div>
                        <div>
                          <div className="text-white font-medium">{name || '—'}</div>
                          <div className="text-dark-100 text-sm">{item.user?.email ?? '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 bg-dark-300 text-dark-100 rounded text-sm">
                        {item.user?.role ?? '—'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-white">{item.totalTasks}</td>
                    <td className="py-4 px-4 text-green-400 font-semibold">{item.completedTasks}</td>
                    <td className="py-4 px-4 text-blue-400 font-semibold">{item.inProgressTasks}</td>
                    <td className="py-4 px-4 text-yellow-400 font-semibold">{item.pendingTasks}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-dark-300 rounded-full h-2 max-w-[100px]">
                          <div
                            className="bg-gradient-to-r from-purple-200 to-purple-300 h-2 rounded-full"
                            style={{ width: `${item.averageProgress}%` }}
                          />
                        </div>
                        <span className="text-white font-semibold text-sm">{item.averageProgress}%</span>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressOverview;

